<template>
  <div class="question-page flex flex-column flex-1">
    <div class="question-page__header flex flex-column">
      <div class="question-page__legend">
        <div>
          Вопрос {{ currentQuestion }} / <span>{{ questionsCount }}</span>
        </div>
        <div class="time-spent">
          {{ timeSpent }}
          <ClockCircleOutlined style="color: var(--main-green)"/>
        </div>
      </div>
      <a-progress :percent="(currentQuestion / questionsCount) * 100" :show-info="false" :stroke-color="['#21AC0C']"/>
    </div>
    <a-card class="ant-card-question">
      <div
        class="
          question-page__body
          flex-1 flex flex-1
          question-page__body
          align-item-start
        "
      >
        <tabs-line-questions
          @updateAnswers="updateAnswers"
          v-if="questionGroups && type === 'list'"
          :questionGroup="questionGroups[currentQuestion - 1]"
          :missedGroup="missedGroup"
          :savedAnswers="currentGroupSavedAnswers"
        />

        <tabs-questions
          @updateAnswers="updateAnswers"
          v-if="questionGroups && type === 'tabs'"
          :questionGroup="questionGroups[currentQuestion - 1]"
          :missedGroup="missedGroup"
          :savedAnswers="currentGroupSavedAnswers"
        />

        <table-question
          @updateAnswers="updateAnswers"
          v-if="questionGroups && type === 'table'"
          :questionGroup="questionGroups[currentQuestion - 1]"
          :missedGroup="missedGroup"
          :savedAnswers="currentGroupSavedAnswers"
        />

        <icons-question
          @updateAnswers="updateAnswers"
          v-if="questionGroups && type === 'icons'"
          :questionGroup="questionGroups[currentQuestion - 1]"
          :missedGroup="missedGroup"
          :savedAnswers="currentGroupSavedAnswers"
        />
      </div>
    </a-card>
    <div
      class="
        question-page__footer
        flex
        justify-content-between
        align-item
        center
      "
    >
      <a-button class="secondary-button" @click="prevPage">
        Назад
      </a-button>
      <a-button class="primary-button" @click="nextPage" :loading="isRequestPending">
        {{ currentQuestion === questionsCount ? 'Завершить тест' : 'Следующий вопрос'}}
      </a-button>
    </div>
  </div>
</template>

<script>
import TabsLineQuestions from "./TabsLineQuestions.vue";
import TabsQuestions from "./TabsQuestions.vue";
import TableQuestion from "./TableQuestion.vue";
import { toRef } from "@vue/reactivity";
import {computed, onBeforeMount, ref, watch} from "@vue/runtime-core";
import IconsQuestion from "./IconsQuestion.vue";
import { notification } from 'ant-design-vue'
import {ClockCircleOutlined} from "@ant-design/icons-vue";

export default {
  components: {
    TabsLineQuestions,
    TabsQuestions,
    TableQuestion,
    IconsQuestion,
    ClockCircleOutlined
  },
  name: "question-template",
  props: {
    currentQuestion: {
      type: Number,
      default: 1,
    },
    questionsCount: {
      type: Number,
      default: 1,
    },
    questionGroups: {
      type: Array,
    },
    isRequestPending: {
      type: Boolean,
      default: false,
    },
    missedGroup: {
      type: Number,
      default: 0,
    },
    startedAt: {
      type: String,
    },
    savedAnswers: {
      type: Array,
      default: () => [],
    }
  },
  setup(props, context) {
    let answers = {};
    let groups = toRef(props, "questionGroups"),
      current = toRef(props, "currentQuestion");

    let type = computed(() => groups.value[current.value - 1].view);
    const prevPage = () => {
      answers = {};
      context.emit("prevPage");
    };
    const nextPage = () => {
      if (
        answers &&
        Object.keys(answers).length ===
          groups.value[current.value - 1]?.questions?.length
      ) {
        context.emit("nextPage", answers);
        answers = {};
      } else {
        openNotification();
      }
    };

    const openNotification = () => {
      notification.open({
        message: `Нужно ответить на все вопросы`,
      });
    }

    const timeSpent = ref('00:00');

    // Получение из дочерних компонентов ответов
    const updateAnswers = (ans) => {
      if (!props.isRequestPending) {
        answers = ans;
      }
    };

    const parseBackendDatetimeLocal = (dateString) => {
      const [datePart, timePart] = dateString.split(" ");
      const [y, m, d] = datePart.split("-").map(Number);
      const [hh, mm, ss] = timePart.split(":").map(Number);
      return new Date(y, m - 1, d, hh, mm, ss);
    }

    const pad2 = (n) => {
      return String(n).padStart(2, "0");
    }

    const formatElapsed = (ms) => {
      const totalSeconds = Math.max(0, Math.floor(ms / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return hours === 0 ? `${pad2(minutes)}:${pad2(seconds)}` : `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
    }

    const startElapsedCounter = (startedAtStr) => {
      const startedAt = startedAtStr ? parseBackendDatetimeLocal(startedAtStr) : new Date();
      let timeoutId = null;
      let intervalId = null;

      const tick = () => {
        const now = new Date();
        const elapsedMs = now - startedAt;
        timeSpent.value = formatElapsed(elapsedMs);
      };

      const now = new Date();
      const msToNextSecond = 1000 - now.getMilliseconds();

      timeoutId = setTimeout(() => {
        tick();
        intervalId = setInterval(tick, 1000);
      }, msToNextSecond);

      tick();

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
    }

    const currentGroupSavedAnswers = computed(() => {
      const currentGroup = groups.value[current.value - 1];
      if (!currentGroup?.questions?.length) return [];

      const questionIds = currentGroup.questions.map(q => q.id);

      return props.savedAnswers.filter(answer =>
          questionIds.includes(answer.question_id)
      );
    });

    const getInitialAnswersForCurrentGroup = () => {
      const currentGroup = groups.value[current.value - 1];
      if (!currentGroup?.questions?.length) return {};

      const questionIds = currentGroup.questions.map(q => q.id);

      return props.savedAnswers
          .filter(item => questionIds.includes(item.question_id))
          .reduce((acc, item) => {
            acc[item.question_id] = item.question_answer_id;
            return acc;
          }, {});
    };

    watch(
        () => [props.currentQuestion, props.savedAnswers],
        () => {
          answers = getInitialAnswersForCurrentGroup();
        },
        { immediate: true, deep: true }
    );

    onBeforeMount(() => {
      startElapsedCounter(props.startedAt);
    });

    return {
      prevPage,
      nextPage,
      updateAnswers,
      answers,
      groups,
      current,
      type,
      timeSpent,
      currentGroupSavedAnswers
    };
  },
};
</script>

<style lang="scss" scoped>
.question-page {
  text-align: left;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  word-break: break-word;

  &__header {
    padding: 0;
    margin-bottom: 20px;

    :deep(.ant-progress-line) {
      margin-bottom: 0 !important;
    }
  }

  &__legend {
    display: flex;
    justify-content: space-between;
    font-size: 16px;
    font-weight: 500;
  }

  &__progress {
    margin-top: 10px;
    height: 3px;
    background-color: var(--cream-color);
    border-radius: 1px;
    overflow: hidden;

    .progress-bar {
      background-color: #59d884;
      transition: width 0.6s ease;
      height: 3px;
    }
  }

  &__body {
    padding: 0;
  }

  &__footer {
    margin-top: 20px;

    .btn {
      padding: 11px 25px;
    }
  }

  @media (max-width: $mobile-breakpoint) {
    &__header {
      padding: 0 20px;
    }

    &__footer {
      padding: 0 20px;
      margin-top: 0;
    }

    .ant-card-question {
      border: unset !important;
    }
  }
}

.time-spent {
  color: var(--main-green);
}
</style>